class CreativeOBDDelayGate {
  constructor(element) {
    this.element = element;
    this.dialog = element.querySelector('[role="dialog"]');
    this.checkboxes = [...element.querySelectorAll('[data-cobd-required-ack]')];
    this.confirmButton = element.querySelector('[data-cobd-delay-confirm]');
    this.error = element.querySelector('#cobd-delay-gate-error');
    this.frequency = element.dataset.frequency || 'every_attempt';
    this.storageKey = element.dataset.storageKey || 'cobd-delay-acknowledged';
    this.pending = null;
    this.returnFocus = null;

    this.handleClick = this.handleClick.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);

    document.addEventListener('click', this.handleClick, true);
    document.addEventListener('submit', this.handleSubmit, true);
    document.addEventListener('keydown', this.handleKeydown);
    this.checkboxes.forEach((checkbox) => checkbox.addEventListener('change', () => this.updateConfirmationState()));
  }

  updateConfirmationState() {
    const approved = this.checkboxes.length > 0 && this.checkboxes.every((checkbox) => checkbox.checked);
    this.confirmButton.disabled = !approved;
    if (approved) this.error.hidden = true;
  }

  hasSessionApproval() {
    if (this.frequency !== 'once_per_session') return false;
    try {
      return sessionStorage.getItem(this.storageKey) === 'true';
    } catch (error) {
      return false;
    }
  }

  rememberApproval() {
    if (this.frequency !== 'once_per_session') return;
    try {
      sessionStorage.setItem(this.storageKey, 'true');
    } catch (error) {
      // Storage can be unavailable in privacy modes; checkout should still continue.
    }
  }

  isCheckoutTrigger(element) {
    if (!element) return false;
    if (element.matches('[data-cobd-checkout-gate], button[name="checkout"], input[name="checkout"]')) return true;
    if (element.matches('a[href]')) {
      try {
        const url = new URL(element.href, window.location.origin);
        return url.origin === window.location.origin && /\/checkout\/?$/.test(url.pathname);
      } catch (error) {
        return false;
      }
    }
    return false;
  }

  handleClick(event) {
    const closeButton = event.target.closest('[data-cobd-delay-close]');
    if (closeButton && this.element.contains(closeButton)) {
      event.preventDefault();
      this.close();
      return;
    }

    const confirmButton = event.target.closest('[data-cobd-delay-confirm]');
    if (confirmButton && this.element.contains(confirmButton)) {
      event.preventDefault();
      this.confirm();
      return;
    }

    const trigger = event.target.closest('[data-cobd-checkout-gate], button[name="checkout"], input[name="checkout"], a[href]');
    if (!this.isCheckoutTrigger(trigger) || this.hasSessionApproval()) return;

    // Replayed checkout clicks must reach app listeners such as Wholesale
    // Pricing Now, which creates the discounted draft checkout.
    if (trigger.dataset.cobdCheckoutApproved === 'true') {
      delete trigger.dataset.cobdCheckoutApproved;
      return;
    }

    const form = this.getForm(trigger);
    if (form && form.dataset.cobdDelayApproved === 'true') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.pending = { trigger, form };
    this.open(trigger);
  }

  handleSubmit(event) {
    const submitter = event.submitter;
    if (!this.isCheckoutTrigger(submitter) || this.hasSessionApproval()) return;
    if (event.target.dataset.cobdDelayApproved === 'true') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.pending = { trigger: submitter, form: event.target };
    this.open(submitter);
  }

  getForm(trigger) {
    if (trigger.form) return trigger.form;
    const formId = trigger.getAttribute('form');
    return formId ? document.getElementById(formId) : null;
  }

  open(trigger) {
    this.returnFocus = trigger || document.activeElement;
    this.checkboxes.forEach((checkbox) => { checkbox.checked = false; });
    this.updateConfirmationState();
    this.error.hidden = true;
    this.element.hidden = false;
    document.body.classList.add('cobd-delay-gate-open');
    requestAnimationFrame(() => this.dialog.focus());
  }

  close() {
    this.element.hidden = true;
    document.body.classList.remove('cobd-delay-gate-open');
    this.pending = null;
    if (this.returnFocus && typeof this.returnFocus.focus === 'function') this.returnFocus.focus();
  }

  confirm() {
    const firstUnchecked = this.checkboxes.find((checkbox) => !checkbox.checked);
    if (firstUnchecked) {
      this.error.hidden = false;
      firstUnchecked.focus();
      return;
    }

    const pending = this.pending;
    this.pending = null;
    this.rememberApproval();
    this.element.hidden = true;
    document.body.classList.remove('cobd-delay-gate-open');

    if (!pending) return;
    if (pending.form) {
      pending.form.dataset.cobdDelayApproved = 'true';
    }

    // Replay the original click after approval. Calling requestSubmit directly
    // bypasses checkout-button listeners installed by wholesale apps and can
    // send the undiscounted cart to Shopify's standard checkout.
    if (pending.trigger && typeof pending.trigger.click === 'function') {
      pending.trigger.dataset.cobdCheckoutApproved = 'true';
      pending.trigger.click();
      return;
    }

    if (pending.form) {
      pending.form.requestSubmit();
      return;
    }

    if (pending.trigger && pending.trigger.href) window.location.assign(pending.trigger.href);
  }

  handleKeydown(event) {
    if (this.element.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;
    const focusable = [...this.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.hidden);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

const creativeOBDDelayGate = document.getElementById('cobd-delay-gate');
if (creativeOBDDelayGate) new CreativeOBDDelayGate(creativeOBDDelayGate);
